import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";

const API_URL = process.env.REACT_APP_API_URL || "https://api.csc-taxi.tech";

const JourneySettingsModal = ({ open, onClose }) => {
  const [settings, setSettings] = useState({
    avg_fuel_consumption_per_100Km: '',
    fuel_price: '',
    profit_percentage: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/journey/setting`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      if (data.status === 1) {
        setSettings(data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/journey/setting/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      const data = await response.json();
      if (data.status === 1) {
        onClose();
      } else {
        setError(data.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      setError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#191932',
          borderRadius: '18px',
          color: 'white',
        },
      }}
    >
      <DialogTitle>
        <VuiTypography variant="h5" color="white" fontWeight="bold">
          Journey Settings
        </VuiTypography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <VuiBox pt={2}>
            <TextField
              fullWidth
              label="Average Fuel Consumption (L/100Km)"
              type="number"
              value={settings.avg_fuel_consumption_per_100Km}
              onChange={handleChange('avg_fuel_consumption_per_100Km')}
              size="small"
              sx={{
                minWidth: 300,
                width: 300,
                marginBottom: '40px',
                height: '1rem !important',
                backgroundColor: 'white',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  color: '#333',
                  border: '1px solid #BDBDBD',
                  '& fieldset': { border: '1px solid #BDBDBD' },
                  '&:hover fieldset': { border: '1px solid #333' },
                  '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                  fontWeight: 400,
                  fontSize: 14,
                  top: '-3px',
                  background: 'transparent !important',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#fff',
                    background: 'transparent !important',
                    px: 0,
                    top: '-3px',
                  },
                  '&.MuiInputLabel-shrink': {
                    color: '#fff',
                    background: 'transparent !important',
                    top: '-6px',
                    left: '-11px',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Fuel Price"
              type="number"
              value={settings.fuel_price}
              onChange={handleChange('fuel_price')}
              size="small"
              sx={{
                minWidth: 300,
                width: 300,
                marginBottom: '40px',
                height: '1rem !important',
                backgroundColor: 'white',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  color: '#333',
                  border: '1px solid #BDBDBD',
                  '& fieldset': { border: '1px solid #BDBDBD' },
                  '&:hover fieldset': { border: '1px solid #333' },
                  '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                  fontWeight: 400,
                  fontSize: 14,
                  top: '-3px',
                  background: 'transparent !important',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#fff',
                    background: 'transparent !important',
                    px: 0,
                    top: '-3px',
                  },
                  '&.MuiInputLabel-shrink': {
                    color: '#fff',
                    background: 'transparent !important',
                    top: '-6px',
                    left: '-11px',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Profit Percentage"
              type="number"
              value={settings.profit_percentage}
              onChange={handleChange('profit_percentage')}
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{
                minWidth: 300,
                width: 300,
                marginBottom: '40px',
                height: '1rem !important',
                backgroundColor: 'white',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  color: '#333',
                  border: '1px solid #BDBDBD',
                  '& fieldset': { border: '1px solid #BDBDBD' },
                  '&:hover fieldset': { border: '1px solid #333' },
                  '&.Mui-focused fieldset': { border: '1.5px solid #333' },
                },
                '& .MuiInputLabel-root': {
                  color: '#333',
                  fontWeight: 400,
                  fontSize: 14,
                  top: '-3px',
                  background: 'transparent !important',
                  px: 0.5,
                  '&.Mui-focused': {
                    color: '#fff',
                    background: 'transparent !important',
                    px: 0,
                    top: '-3px',
                  },
                  '&.MuiInputLabel-shrink': {
                    color: '#fff',
                    background: 'transparent !important',
                    top: '-6px',
                    left: '-11px',
                  },
                },
              }}
            />
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </VuiBox>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: 'flex-end' }}>
        <Button 
          onClick={onClose}
          sx={{ 
            color: 'white !important',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{ 
            backgroundColor: '#2196f3',
            color: 'white !important',
            '&:hover': {
              backgroundColor: '#1976d2',
            },
          }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JourneySettingsModal; 